import React, { useState, useEffect } from 'react';
import { useQuestions } from '../../Context/QuestionContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, Edit2, Trash2, Eye, Filter, Database, Image as ImageIcon } from 'lucide-react';
import { Table, Button, Form, InputGroup, Badge, Modal, Row, Col, Card, Pagination } from 'react-bootstrap';
import QuestionFormModal from '../../Components/QuestionFormModal';
import { getMediaUrl } from '../../Services/apiService';

const Swal = require('sweetalert2');

const QuestionPool = () => {
    const { questions, deleteQuestion, addQuestion, updateQuestion } = useQuestions();
    const [searchTerm, setSearchTerm] = useState('');
    const [filterDifficulty, setFilterDifficulty] = useState('ALL');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [previewQuestion, setPreviewQuestion] = useState(null);

    const filteredQuestions = questions.filter(q => {
        const matchesSearch = q.text.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesDifficulty = filterDifficulty === 'ALL' || q.difficulty === filterDifficulty;
        return matchesSearch && matchesDifficulty;
    });

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterDifficulty]);

    const totalPages = Math.ceil(filteredQuestions.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentQuestions = filteredQuestions.slice(indexOfFirstItem, indexOfLastItem);

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Emin misiniz?',
            text: "Bu soruyu havuzdan silmek istediğinize emin misiniz? Bu işlem geri alınamaz!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Evet, sil!',
            cancelButtonText: 'İptal'
        }).then((result) => {
            if (result.isConfirmed) {
                deleteQuestion(id);
                Swal.fire(
                    'Silindi!',
                    'Soru havuzdan başarıyla silindi.',
                    'success'
                );
            }
        });
    };

    const handleSave = async (questionData) => {
        console.log('[QuestionPool] handleSave called with:', questionData);
        
        if (editingQuestion) {
            console.log('[QuestionPool] Updating question:', editingQuestion.id);
            await updateQuestion(editingQuestion.id, questionData);
            Swal.fire({
                title: 'Güncellendi!',
                text: 'Soru başarıyla güncellendi.',
                icon: 'success',
                timer: 1500,
                showConfirmButton: false
            });
            setEditingQuestion(null);
        } else {
            console.log('[QuestionPool] Creating new question...');
            try {
                const result = await addQuestion(questionData);
                console.log('[QuestionPool] addQuestion result:', result);
                Swal.fire({
                    title: 'Oluşturuldu!',
                    text: 'Yeni soru havuza eklendi.',
                    icon: 'success',
                    timer: 1500,
                    showConfirmButton: false
                });
            } catch (error) {
                console.error('[QuestionPool] addQuestion error:', error);
                Swal.fire({
                    title: 'Hata!',
                    text: 'Soru eklenirken hata oluştu: ' + error.message,
                    icon: 'error'
                });
                return;
            }
        }
        setShowCreateModal(false);
    };

    const openEditModal = (q) => {
        setEditingQuestion(q);
        setShowCreateModal(true);
    };

    const openCreateModal = () => {
        setEditingQuestion(null);
        setShowCreateModal(true);
    };

    return (
        <div className="py-2">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                <div>
                    <h2 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                         <Database size={28} className="text-primary"/> Soru Havuzu
                    </h2>
                    <p className="text-muted mb-0">Tüm soruları buradan yönetebilirsiniz.</p>
                </div>
                <Button 
                    variant="primary" 
                    className="d-flex align-items-center justify-content-center gap-2 shadow-sm border-0 px-4 py-2"
                    onClick={openCreateModal}
                >
                    <Plus size={18} />
                    Yeni Soru Ekle
                </Button>
            </div>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="p-3 bg-light rounded-3">
                    <Row className="g-3">
                        <Col md={8}>
                            <InputGroup>
                                <InputGroup.Text className="bg-white border-end-0">
                                    <Search size={18} className="text-muted"/>
                                </InputGroup.Text>
                                <Form.Control
                                    placeholder="Soru metni ile ara..."
                                    className="border-start-0 shadow-none"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </InputGroup>
                        </Col>
                        <Col md={4}>
                            <InputGroup>
                                <InputGroup.Text className="bg-white border-end-0">
                                    <Filter size={18} className="text-muted"/>
                                </InputGroup.Text>
                                <Form.Select 
                                    className="border-start-0 shadow-none cursor-pointer"
                                    value={filterDifficulty}
                                    onChange={(e) => setFilterDifficulty(e.target.value)}
                                >
                                    <option value="ALL">Tüm Zorluklar</option>
                                    <option value="EASY">Kolay</option>
                                    <option value="MEDIUM">Orta</option>
                                    <option value="HARD">Zor</option>
                                </Form.Select>
                            </InputGroup>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm">
                <div className="table-responsive">
                    <Table hover className="mb-0 align-middle">
                        <thead className="bg-light">
                            <tr>
                                <th className="py-3 ps-4 border-0 text-secondary small">Soru</th>
                                <th className="py-3 border-0 text-secondary small d-none d-lg-table-cell">Tür</th>
                                <th className="py-3 border-0 text-secondary small d-none d-sm-table-cell">Zorluk</th>
                                <th className="py-3 border-0 text-secondary small d-none d-lg-table-cell">Medya</th>
                                <th className="py-3 pe-4 text-end border-0 text-secondary small">İşlemler</th>
                            </tr>
                        </thead>
                        <tbody>
                            <AnimatePresence>
                                {currentQuestions.map(q => (
                                    <motion.tr 
                                        key={q.id}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        layout
                                    >
                                        <td className="ps-4">
                                            <div className="fw-medium text-dark text-truncate" style={{maxWidth: '200px'}} title={q.text}>{q.text}</div>
                                            <div className="small text-muted">
                                                {q.type === 'MULTIPLE_CHOICE' ? `${q.options ? q.options.length : 0} Seçenek` : 'Klasik'}
                                            </div>
                                        </td>
                                        <td>
                                            <Badge bg="light" text="dark" className="border">
                                                {q.type === 'MULTIPLE_CHOICE' ? 'Çoktan Seçmeli' : 'Klasik'}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Badge bg={q.difficulty === 'EASY' ? 'success' : q.difficulty === 'MEDIUM' ? 'warning' : 'danger'}>
                                                {q.difficulty === 'EASY' ? 'Kolay' : q.difficulty === 'MEDIUM' ? 'Orta' : 'Zor'}
                                            </Badge>
                                        </td>
                                        <td>
                                            {q.media ? (
                                                <div className="d-flex align-items-center justify-content-center bg-light rounded border" style={{width: 40, height: 40, overflow: 'hidden'}}>
                                                    {q.media.type === 'IMAGE' ? (
                                                        <img 
                                                            src={getMediaUrl(q.media.url)} 
                                                            alt="media" 
                                                            className="w-100 h-100 object-fit-cover" 
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                e.target.nextSibling.style.display = 'flex';
                                                            }}
                                                        />
                                                    ) : (
                                                        <Badge bg="info" className="text-white" style={{fontSize: '0.6rem'}}>VIDEO</Badge>
                                                    )}
                                                    <div className="d-none justify-content-center align-items-center w-100 h-100 bg-secondary bg-opacity-10 text-secondary" title="Medya bulunamadı veya hatalı">
                                                        <ImageIcon size={16} />
                                                    </div>
                                                </div>
                                            ) : (
                                                <span className="text-muted small">-</span>
                                            )}
                                        </td>
                                        <td className="text-end pe-4">
                                            <div className="d-flex align-items-center justify-content-end gap-2">
                                                <Button 
                                                    variant="light" 
                                                    size="sm" 
                                                    className="text-primary d-flex align-items-center justify-content-center p-0" 
                                                    style={{width: 32, height: 32, borderRadius: '6px'}}
                                                    onClick={() => setPreviewQuestion(q)}
                                                    title="Önizle"
                                                >
                                                    <Eye size={16} />
                                                </Button>
                                                <Button 
                                                    variant="light" 
                                                    size="sm" 
                                                    className="text-warning d-flex align-items-center justify-content-center p-0"
                                                    style={{width: 32, height: 32, borderRadius: '6px'}} 
                                                    onClick={() => openEditModal(q)}
                                                    title="Düzenle"
                                                >
                                                    <Edit2 size={16} />
                                                </Button>
                                                <Button 
                                                    variant="light" 
                                                    size="sm" 
                                                    className="text-danger d-flex align-items-center justify-content-center p-0"
                                                    style={{width: 32, height: 32, borderRadius: '6px'}} 
                                                    onClick={() => handleDelete(q.id)}
                                                    title="Sil"
                                                >
                                                    <Trash2 size={16} />
                                                </Button>
                                            </div>
                                        </td>
                                    </motion.tr>
                                ))}
                            </AnimatePresence>
                            {filteredQuestions.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="text-center py-5 text-muted">
                                        Aradığınız kriterlere uygun soru bulunamadı.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
                {totalPages > 1 && (
                    <div className="d-flex justify-content-center p-3 border-top">
                        <Pagination className="mb-0">
                            <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                            <Pagination.Prev onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} />
                            
                            {[...Array(totalPages)].map((_, idx) => {
                                // Add a simple logic to show limited pagination items if there are too many pages
                                // Just a simple approach: show all pages for now since it's an internal admin tool,
                                // but typically you'd want ellipsis for many pages.
                                return (
                                    <Pagination.Item 
                                        key={idx + 1} 
                                        active={idx + 1 === currentPage}
                                        onClick={() => setCurrentPage(idx + 1)}
                                    >
                                        {idx + 1}
                                    </Pagination.Item>
                                );
                            })}

                            <Pagination.Next onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} />
                            <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                        </Pagination>
                    </div>
                )}
            </Card>

            {/* CREATE/EDIT MODAL */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" centered backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">
                        {editingQuestion ? 'Soruyu Düzenle' : 'Yeni Soru Oluştur'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <QuestionFormModal 
                        onClose={() => setShowCreateModal(false)} 
                        onSave={handleSave} 
                        prefillData={editingQuestion}
                    />
                </Modal.Body>
            </Modal>

            {/* PREVIEW MODAL */}
            <Modal show={!!previewQuestion} onHide={() => setPreviewQuestion(null)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Önizleme</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center p-4">
                    {previewQuestion && (
                        <>
                             {previewQuestion.media && (
                                <div className="mb-4 d-flex justify-content-center bg-dark rounded p-3" style={{maxHeight: 300}}>
                                     {previewQuestion.media.type === 'IMAGE' ? (
                                         <>
                                             <img 
                                                src={getMediaUrl(previewQuestion.media.url)} 
                                                className="img-fluid object-fit-contain" 
                                                style={{maxHeight: '100%'}}
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.nextSibling.style.display = 'flex';
                                                }}
                                             />
                                             <div className="d-none justify-content-center align-items-center flex-column text-muted py-4">
                                                 <ImageIcon size={48} className="mb-2 opacity-50" />
                                                 <p className="mb-0">Medya yüklenemedi (Geçersiz URL veya silinmiş dosya)</p>
                                             </div>
                                         </>
                                     ) : (
                                         <div className="text-white">Video: {previewQuestion.media.url}</div>
                                     )}
                                </div>
                             )}
                             <h4 className="fw-bold mb-3">{previewQuestion.text}</h4>
                             {previewQuestion.type === 'MULTIPLE_CHOICE' && previewQuestion.options && (
                                 <div className="d-grid gap-2 text-start mw-50 mx-auto">
                                     {previewQuestion.options.map((opt, i) => {
                                         const isCorrect = (previewQuestion.correctAnswers && previewQuestion.correctAnswers.includes(opt)) || 
                                                           (previewQuestion.correctAnswer && previewQuestion.correctAnswer === opt);
                                                           
                                         return (
                                             <div key={i} className={`p-2 border rounded ${isCorrect ? 'bg-success text-white border-success' : 'bg-light'}`}>
                                                 {opt}
                                                 {isCorrect && previewQuestion.correctAnswers && previewQuestion.correctAnswers.length > 1 && <span className="small ms-2">(Doğru)</span>}
                                             </div>
                                         );
                                     })}
                                     {previewQuestion.correctAnswers && previewQuestion.correctAnswers.length > 1 && (
                                         <div className="text-center text-secondary small mt-2 fst-italic">
                                             (Bu sorunun birden fazla doğru cevabı vardır)
                                         </div>
                                     )}
                                 </div>
                             )}
                             {previewQuestion.type !== 'MULTIPLE_CHOICE' && (
                                 <div className="p-3 bg-light rounded text-success fw-bold d-inline-block px-5">
                                     Cevap: {Array.isArray(previewQuestion.correctAnswers) ? previewQuestion.correctAnswers.join(', ') : previewQuestion.correctAnswer}
                                 </div>
                             )}
                        </>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default QuestionPool;
